import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    useTheme,
    useMediaQuery,
    CircularProgress,
    LinearProgress,
    Paper,
    IconButton
} from '@mui/material';
import {
    IconBolt,
    IconCheck,
    IconStar,
    IconFlame,
    IconTrendingUp,
    IconInfoCircle,
    IconHistory,
    IconClock
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors, fonts } from '../../ColorFont/color';
import { url, rzkey } from '../../ConstUrl/Url';
import { loadRazorpay } from '../../utils/loadRazorpay';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useUser } from '../../context/UserContext';

const BoostYourListing = () => {
    const { t } = useTranslation(['common', 'dashboard']);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();

    // The plot to boost is passed via location state
    const { cropData: initialCropData } = location.state || {};
    
    const { userData, setUserData } = useUser();
    const [userCrops, setUserCrops] = useState([]);
    const [selectedCrop, setSelectedCrop] = useState(initialCropData || null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);

    useEffect(() => {
        fetchPlans();
        if (!initialCropData) {
            fetchUserCrops();
        }
    }, []);

    const fetchUserCrops = async () => {
        try {
            const response = await fetch(`${url}/api/plots?user=${userData._id}`);
            const data = await response.json();
            const plots = data.paginatedPlots || data;
            if (Array.isArray(plots)) {
                setUserCrops(plots);
                if (plots.length > 0) setSelectedCrop(plots[0]);
            }
        } catch (error) {
            console.error("Error fetching user crops:", error);
        }
    };

    const fetchPlans = async () => {
        try {
            const response = await fetch(`${url}/api/get-seller-plans`);
            const data = await response.json();
            setPlans(data);
            if (data.length > 0) {
                // Default selection (middle plan if exists)
                setSelectedPlan(data[1]?._id || data[0]?._id);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
            toast.error(t('failedToLoadPlans'));
        } finally {
            setLoading(false);
        }
    };

    const handlePayment = async (planToBuy = null) => {
        const plan = planToBuy || plans.find(p => p._id === selectedPlan);
        
        if (!plan) {
            toast.warning(t('pleaseSelectPlanFirst'));
            return;
        }
        if (!selectedCrop) {
            toast.error(t('noCropSelectedForBoost'));
            return;
        }

        setProcessingPayment(true);
        try {
            // 1. Create Order
            const orderRes = await axios.post(`${url}/create-order`, {
                amount: plan.discountedPrice || plan.originalPrice,
                currency: "INR"
            });

            const { id: order_id, amount, currency } = orderRes.data;

        // 2. Load Razorpay on-demand and open checkout
            const razorpayLoaded = await loadRazorpay();
            if (!razorpayLoaded) {
                toast.error(t('failedToLoadPaymentGateway'));
                return;
            }

            // 3. Open Razorpay
            const options = {
                key: rzkey,
                amount: amount,
                currency: currency,
                name: "KrushiSetu",
                description: `Purchase ${plan.name} - ${plan.sponseredHrs} Boost Hours`,
                order_id: order_id,
                handler: async (response) => {
                    try {
                        // 3. Verify Payment
                        const verifyRes = await axios.post(`${url}/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        if (verifyRes.data.success) {
                            // 4. Update Plan on User account (Adds Sponsored Hours)
                            const buyRes = await axios.post(`${url}/api/buy-seller-plan`, {
                                userId: userData._id,
                                planId: plan._id
                            });

                            if (buyRes.status === 200) {
                                toast.success(t('planActivatedSuccess'));
                                // Fetch updated user data
                                const updatedUserRes = await axios.get(`${url}/api/users/get-user-by-id/${userData._id}`);
                                localStorage.setItem('userData', JSON.stringify(updatedUserRes.data));
                                setUserData(updatedUserRes.data);
                                setSelectedPlan(null); // Reset after purchase
                            }
                        }
                    } catch (err) {
                        console.error("Activation failed:", err);
                        toast.error(t('paymentProcessFailed'));
                    }
                },
                prefill: {
                    name: userData?.name || "",
                    email: userData?.email || "",
                    contact: userData?.phoneNumber || ""
                },
                theme: { color: colors.primary }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (r) => toast.error(t('paymentFailedPrefix') + r.error.description));
            rzp.open();
        } catch (err) {
            console.error("Payment setup failed:", err);
            toast.error(t('failedToInitiatePayment'));
        } finally {
            setProcessingPayment(false);
        }
    };


    const benefits = [
        "Appear at top of search results",
        "Featured in Sponsored section on homepage",
        "Stand out with Sponsored badge",
        "Get 5x more views"
    ];

    const getPlanIcon = (name) => {
        if (name.toLowerCase().includes('week')) return { icon: IconTrendingUp, color: '#10B981', bg: '#F0FDF4' };
        if (name.toLowerCase().includes('day')) return { icon: IconFlame, color: '#EF4444', bg: '#FEF2F2' };
        return { icon: IconStar, color: '#F59E0B', bg: '#FFFBEB' };
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    const currentHours = userData?.sponseredHours || 0;
    const progress = (currentHours / 100) * 100; // Just as example

    return (
        <Box sx={{
            padding: 0,
            maxWidth: '1600px',
            margin: '0 auto',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            backgroundColor: '#FCFDFC'
        }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <Box sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: colors.primary_rgba_8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.primary,
                    mb: '8px'
                }}>
                    <IconBolt size={32} stroke={1.5} />
                </Box>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 800,
                    fontSize: isMobile ? '24px' : '30px',
                    lineHeight: '36px',
                    color: '#0F1729',
                    textAlign: 'center'
                }}>
                    {t('boostYourListing')}
                </Typography>
                <Typography sx={{
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 400,
                    fontSize: '15px',
                    color: '#637381',
                    textAlign: 'center'
                }}>
                    {t('sellProduceFaster')}
                </Typography>
            </Box>

            {/* Current Plan Banner */}
            <Box sx={{
                width: '100%',
                background: 'linear-gradient(135deg, #00A76F 0%, #007867 100%)',
                borderRadius: '24px',
                padding: isMobile ? '28px 20px' : '40px 48px',
                color: '#FFFFFF',
                position: 'relative',
                boxShadow: '0px 12px 24px -4px rgba(0, 144, 99, 0.16)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '32px' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '8px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '16px', opacity: 0.95, fontFamily: fonts.poppins }}>{t('currentPlan')}</Typography>
                            <IconButton 
                                size="small" 
                                onClick={() => navigate('/purchase-history')}
                                sx={{ color: '#FFFFFF', padding: 0, opacity: 0.8, '&:hover': { opacity: 1 } }}
                            >
                                <IconInfoCircle size={18} />
                            </IconButton>
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: isMobile ? '36px' : '48px', lineHeight: 1, fontFamily: fonts.poppins }}>
                            {userData?.sponseredHours > 0 ? t('premium') : t('noActivePlan')}
                        </Typography>
                    </Box>
                    <Box sx={{
                        backgroundColor: '#FFFFFF',
                        color: userData?.sponseredHours > 0 ? colors.primary : '#637381',
                        padding: '8px 18px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
                    }}>
                        <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: userData?.sponseredHours > 0 ? colors.primary : '#637381' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, fontFamily: fonts.poppins }}>
                            {userData?.sponseredHours > 0 ? t('active') : t('inactive')}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={4} sx={{ mb: '32px' }}>
                    <Grid item xs={6} sm={4}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, opacity: 0.8, mb: '4px', textTransform: 'uppercase', fontFamily: fonts.poppins }}>{t('boostHoursLabel')}</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, fontFamily: fonts.poppins }}>{currentHours} hrs</Typography>
                    </Grid>
                    {userData?.sponseredHours > 0 && (
                        <Grid item xs={6} sm={4}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, opacity: 0.8, mb: '4px', textTransform: 'uppercase' }}>{t('status')}</Typography>
                            <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>{t('promoting')}</Typography>
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ width: '100%', position: 'relative', pt: '10px' }}>
                    <Box sx={{ width: '100%', height: '8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(100, progress)}%`, height: '100%', backgroundColor: '#FFFFFF', borderRadius: '4px' }} />
                    </Box>
                </Box>
            </Box>


            {/* Existing Multi-Plan Purchase Section (for balance recharge) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', mb: '40px' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#171C17' }}>{t('needMoreHours')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {plans.map((plan) => {
                        const { icon: PlanIcon, color: iconColor, bg: iconBg } = getPlanIcon(plan.name);
                        const isSelected = selectedPlan === plan._id;
                        
                        return (
                            <Box
                                key={plan._id}
                                onClick={() => {
                                    if (processingPayment) return;
                                    setSelectedPlan(plan._id);
                                }}
                                sx={{
                                    padding: '24px',
                                    borderRadius: '20px',
                                    border: `2px solid ${isSelected ? colors.primary : colors.border}`,
                                    backgroundColor: isSelected ? colors.primary_rgba_4 : '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '16px',
                                    cursor: processingPayment ? 'default' : 'pointer',
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    '&:hover': { 
                                        transform: processingPayment ? 'none' : 'translateY(-2px)', 
                                        borderColor: colors.primary 
                                    }
                                }}
                            >
                                {plan.isMostPopular && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: '-12px',
                                        left: '24px',
                                        background: '#B71D18',
                                        color: '#FFF',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '50px',
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        zIndex: 1
                                    }}>
                                        {t('dashboard:mostPopular')}
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <Box sx={{ 
                                        width: '56px', height: '56px', borderRadius: '16px', 
                                        backgroundColor: iconBg, display: 'flex', alignItems: 'center', 
                                        justifyContent: 'center', color: iconColor 
                                    }}>
                                        <PlanIcon size={28} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 800, fontSize: '16px', fontFamily: fonts.poppins }}>{plan.name}</Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#637381', fontFamily: fonts.poppins }}>{plan.sponseredHrs}{t('hoursVisibility')}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '20px', color: colors.primary, fontFamily: fonts.poppins }}>
                                        ₹{plan.discountedPrice || plan.originalPrice}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#637381', fontFamily: fonts.poppins }}>
                                        {plan.sponseredHrs} {t('HOURS_upper')}
                                    </Typography>
                                </Box>

                                {isSelected && (
                                    <Box sx={{
                                        position: 'absolute',
                                        right: -10,
                                        top: -10,
                                        backgroundColor: colors.primary,
                                        color: '#FFF',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}>
                                        <IconCheck size={16} stroke={4} />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Buy Button for Selected Plan */}
                {selectedPlan && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {!selectedCrop && (
                            <Typography sx={{ fontSize: '13px', color: '#FF4842', textAlign: 'center', mb: 1, fontWeight: 600 }}>
                                {t('selectListingToBoost')}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => handlePayment(plans.find(p => p._id === selectedPlan))}
                            disabled={processingPayment}
                            startIcon={processingPayment ? <CircularProgress size={20} color="inherit" /> : <IconBolt size={20} />}
                            sx={{
                                width: isMobile ? '100%' : 'auto',
                                minWidth: isMobile ? '100%' : '420px',
                                height: '52px',
                                borderRadius: '14px',
                                backgroundColor: colors.primary,
                                backgroundImage: colors.button,
                                fontWeight: 700,
                                fontSize: isMobile ? '14px' : '16px',
                                textTransform: 'none',
                                px: 4,
                                boxShadow: '0px 8px 16px colors.primary_rgba_24',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0px 12px 24px rgba(22, 162, 73, 0.35)',
                                    transform: 'translateY(-2px)'
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.7,
                                    color: 'rgba(255,255,255,0.7)'
                                }
                            }}
                        >
                            {processingPayment ? t('processing') : `${t('purchasePrefix')}${plans.find(p => p._id === selectedPlan)?.name}${t('boostCreditsSuffix')}`}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Purchase History Link */}
        </Box>
    );
};

export default BoostYourListing;
